import React, { useId, useMemo, useCallback } from 'react'
import {
  Controller,
  useFieldArray,
  UseFieldArrayRemove,
  useFormContext,
} from 'react-hook-form'
import { FiltersSchema } from '../filtersSchema'
import {
  AsyncSelect,
  Box,
  Button,
  Field,
  Flex,
  useAppTheme,
} from '@workwhile/ui'
import { CircleMinus } from 'lucide-react'
import { useShiftsContextValue } from 'pages/Shifts/Shifts/context/ShiftsProvider'
import { indexBy, prop } from 'ramda'
import { NotSetAccountOrSalesOwnerFilterId } from 'api/shifts/getAllShifts'
import { FullOptions, MatchData, Searcher } from 'fast-fuzzy'

const NotSetAccountOwnerOption = {
  label: 'Not set',
  value: NotSetAccountOrSalesOwnerFilterId,
}

const AddedAccountOwnerRow = ({
  remove,
  index,
  value,
  isLast,
}: {
  remove: UseFieldArrayRemove
  index: number
  value: string
  isLast: boolean
}) => {
  const { colors, radii, space } = useAppTheme()
  return (
    <Flex
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
      gap={space[2]}
      mb={isLast ? 0 : space[2]}
    >
      <Flex
        alignItems="flex-start"
        justifyContent="space-between"
        border="1px solid"
        borderColor={colors.neutrals[100]}
        p={2}
        borderRadius={radii.small}
        width="100%"
        position="relative"
      >
        {value}
      </Flex>
      <Button
        onClick={() => remove(index)}
        variant="text"
        color="red"
        size="small"
        danger
        style={{
          margin: 0,
          height: '1.6rem',
        }}
      >
        <CircleMinus size={16} />
      </Button>
    </Flex>
  )
}

const AddedAccountOwners = () => {
  const baseId = useId()
  const { watch, control } = useFormContext<FiltersSchema>()
  const {
    state: { accountOwners },
  } = useShiftsContextValue()

  const { remove } = useFieldArray({
    name: 'company.accountOwnerIds',
    control,
  })

  const accountOwnerIds = watch('company.accountOwnerIds') ?? []

  const accountOwnerMap = useMemo(() => {
    if (!accountOwners) return {}
    const groupedAccountOwners = indexBy(prop('id'), accountOwners)
    return groupedAccountOwners
  }, [accountOwners])

  const addedAccountOwnerRows = accountOwnerIds.map(({ id }, index) => (
    <AddedAccountOwnerRow
      key={`added-account-owner-${baseId}-${id}-${index}`}
      remove={remove}
      index={index}
      value={
        id === NotSetAccountOrSalesOwnerFilterId
          ? 'No Account Owner'
          : `${accountOwnerMap[id]?.name} (${id})`
      }
      isLast={index === accountOwnerIds.length - 1}
    />
  ))
  return (
    <Box width="256px" maxHeight="360px" overflow="auto">
      {addedAccountOwnerRows}
    </Box>
  )
}

type AccountOwnerOption = {
  label: string
  value: number | typeof NotSetAccountOrSalesOwnerFilterId
}

const fuzzyOptions: FullOptions<AccountOwnerOption> = {
  threshold: 0.7,
  ignoreCase: true,
  ignoreSymbols: true,
  normalizeWhitespace: true,
  returnMatchData: false,
  keySelector: (obj) => [obj.label, obj.value.toString()],
}

export const AccountOwnersFilter = () => {
  const { control, trigger, getValues, setValue, resetField } =
    useFormContext<FiltersSchema>()
  const {
    state: { accountOwners },
  } = useShiftsContextValue()

  const accountOwnerOptions = useMemo(() => {
    const options =
      accountOwners?.map((accountOwner) => ({
        label: `${accountOwner.name} (${accountOwner.id})`,
        value: accountOwner.id,
      })) || []
    return [NotSetAccountOwnerOption, ...options]
  }, [accountOwners])

  const searcher = useMemo(() => {
    return new Searcher<AccountOwnerOption, FullOptions<AccountOwnerOption>>(
      accountOwnerOptions ?? [],
      fuzzyOptions
    )
  }, [accountOwnerOptions])

  const loadOptions = useCallback(
    (rawInput: string) => {
      if (!rawInput) return true

      const results = searcher.search(
        rawInput,
        fuzzyOptions
      ) as unknown as MatchData<AccountOwnerOption>[]

      return new Promise((resolve) => {
        resolve(results)
      })
    },
    [searcher]
  )

  const { append } = useFieldArray({
    name: 'company.accountOwnerIds',
    control,
  })

  return (
    <Box>
      <Controller
        control={control}
        name="company.accountOwnerIdUserInput"
        defaultValue={undefined}
        render={({ field, fieldState: { error, invalid } }) => (
          <Field error={error?.message?.toString()}>
            <Flex gap={2} width="256px" maxHeight="360px">
              <AsyncSelect
                block
                {...field}
                isClearable
                isSearchable
                defaultOptions={accountOwnerOptions}
                loadOptions={loadOptions}
                value={
                  accountOwnerOptions.find(
                    (option) => option.value === field.value
                  ) || null
                }
                onChange={(option) => {
                  field.onChange(option?.value)
                }}
                styles={{
                  container: (base) => ({
                    ...base,
                    width: '100%',
                  }),
                }}
              />
              <Button
                variant="primary"
                disabled={invalid}
                onClick={async () => {
                  const isInputValid = await trigger(
                    'company.accountOwnerIdUserInput'
                  )
                  const accountOwnerId = getValues(
                    'company.accountOwnerIdUserInput'
                  )
                  const addedAccountOwners =
                    getValues('company.accountOwnerIds') ?? []
                  const doesInputExistInCurrentGroup = addedAccountOwners.some(
                    (accountOwner) => accountOwner.id === accountOwnerId
                  )

                  const isValid =
                    isInputValid &&
                    !doesInputExistInCurrentGroup &&
                    (accountOwnerId === NotSetAccountOrSalesOwnerFilterId ||
                      !Number.isNaN(Number(accountOwnerId))) &&
                    typeof accountOwnerId !== 'undefined'

                  if (isValid) {
                    append({ id: accountOwnerId })
                    resetField('company.accountOwnerIdUserInput', {
                      defaultValue: undefined,
                    })
                    setValue('company.accountOwnerIdUserInput', undefined)
                  }

                  if (doesInputExistInCurrentGroup) {
                    resetField('company.accountOwnerIdUserInput', {
                      defaultValue: undefined,
                    })
                  }
                }}
              >
                Add
              </Button>
            </Flex>
          </Field>
        )}
      />

      <AddedAccountOwners />
    </Box>
  )
}
